import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Users, Baby, Wheat, HeartHandshake,
  Landmark, School, Tent, Sun, ShieldCheck, Recycle,
  Download, FileText, ChevronDown, Bug,
} from "lucide-react";
import { GLOBAL_HEALTH, ASSETS } from "@/lib/assets";
import { Reveal } from "@/components/Section";
import { EarthShowcase } from "@/components/EarthShowcase";
import { useIsMobile } from "@/components/HeroStory";
import ZoomImage from "@/components/ZoomImage";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const AMBER = "#f5a524";
const AMBER_SOFT = "#ffca62";
const ICONS = { Users, Baby, Wheat, HeartHandshake, Landmark, School, Tent };
const GH = GLOBAL_HEALTH;

const A = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";
const GH_PDF = "/docs/WearSpray_Global_Health_Initiative.pdf";
const GH_WHITEPAPER = "/docs/Combating-Malaria-with-WearSpray-Technology.pdf";
const GH_MARKET_PDF = "/docs/WearSpray_Market_Opportunity_Strategy_Brief.pdf";
const GH_HERO_IMAGES = [
  { src: `${A}pakdvb15_farm%20worker.jpg`, alt: "An agricultural worker standing in his crop field, wearing a WearSpray band on his wrist", pos: "center", posM: "80% 45%" },
  { src: `${A}w4hgn8js_health%20care%20dr%20india.jpg`, alt: "A doctor in navy scrubs in a hospital, wearing a teal WearSpray band", pos: "center", posM: "72% 42%" },
  { src: `${A}yovmxa63_nurse%20india.jpg`, alt: "A nurse in blue scrubs beside a sunlit hospital window, wearing a WearSpray band", pos: "55% 58%", posM: "70% 54%" },
  { src: `${A}54k5vw3r_health%20care%202%20india.jpg`, alt: "A community health worker at her clinic desk, wearing a pink WearSpray band", pos: "58% 60%", posM: "66% 58%" },
];

const SOLUTION_FEATURES = [
  { icon: Sun, title: "All Day", body: "Covers the daylight exposure window." },
  { icon: ShieldCheck, title: "Complements Nets", body: "Adds to existing prevention, doesn't compete." },
  { icon: Recycle, title: "Reusable", body: "One durable band, refilled again and again." },
];

function Overline({ children }) {
  return (
    <span className="font-mono text-xs tracking-[0.2em] uppercase block mb-4" style={{ color: AMBER }}>
      {children}
    </span>
  );
}

function GHHero() {
  const [active, setActive] = useState(0);
  const isMobile = useIsMobile();
  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % GH_HERO_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="global-health-hero-section"
    >
      {/* Full-bleed banner crossfade */}
      <div className="absolute inset-0 z-0" data-testid="global-health-hero-photo">
        {GH_HERO_IMAGES.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={i === active ? img.alt : ""}
            aria-hidden={i !== active}
            loading={i === 0 ? "eager" : "lazy"}
            className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
            style={{
              opacity: i === active ? 1 : 0,
              transitionDuration: "1800ms",
              objectPosition: isMobile ? img.posM || "55% 50%" : img.pos || "center",
            }}
          />
        ))}
        {/* Legibility scrims, kept dark on the left for text, lighter on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/75 via-transparent to-[#0a0a0a]/35" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-mono text-xs tracking-[0.25em] uppercase mb-6"
          style={{ color: AMBER }}
          data-testid="global-health-hero"
        >
          Global Health Initiative
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.0] max-w-4xl"
        >
          From Personal Protection to{" "}
          <span style={{ color: AMBER_SOFT }}>Population Impact.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
          className="font-body text-lg md:text-xl text-zinc-200 mt-8 max-w-2xl leading-relaxed"
        >
          A patented wearable dispensing platform designed to help
          organizations make essential liquid, lotion, and gel solutions
          immediately accessible across workforces, communities, and public
          health environments.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.38 }}
          className="font-heading text-xl md:text-3xl font-light tracking-tight leading-snug mt-6 max-w-2xl"
          style={{ color: AMBER_SOFT }}
          data-testid="global-health-hero-question"
        >
          What if protection could travel with the person?
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.52 }}
          className="mt-10"
        >
          <Link
            to="/contact"
            data-testid="global-health-hero-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium text-black px-7 py-4 transition-colors"
            style={{ backgroundColor: AMBER }}
          >
            Partner on Global Health
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 pointer-events-none" data-testid="global-health-scroll-cue">
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-300">Scroll to explore</span>
        <ChevronDown size={18} className="animate-scroll-cue" style={{ color: AMBER }} />
      </div>
    </section>
  );
}

const GH_PRECURSOR = [
  <>A new delivery platform for <span style={{ color: AMBER_SOFT }}>public health protection</span>.</>,
  <>Built for deployment across <span style={{ color: AMBER_SOFT }}>workforces and communities</span>.</>,
  <>Designed to scale through the <span style={{ color: AMBER_SOFT }}>systems already serving them</span>.</>,
];

function GHPrecursor() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % GH_PRECURSOR.length), 3800);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      className="border-t border-zinc-800 bg-[#0a0a0a] overflow-hidden"
      data-testid="global-health-precursor"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Overline>A New Delivery Model</Overline>
        <div className="relative h-[130px] sm:h-[150px] md:h-[170px]">
          <AnimatePresence>
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -16, filter: "blur(10px)" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-center font-heading text-3xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.05]"
              data-testid="global-health-precursor-text"
            >
              <span className="max-w-4xl">{GH_PRECURSOR[i]}</span>
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function GHStats() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>The Daytime Gap</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Protection fails not because it doesn't exist, but because it isn't there in the moment.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16 bg-zinc-800 border border-zinc-800">
          {GH.stats.map((s, i) => (
            <Reveal key={s.value} delay={i * 0.1}>
              <div className="bg-[#121212] p-8 md:p-10 h-full">
                <p className="font-heading text-4xl md:text-5xl font-light" style={{ color: AMBER_SOFT }}>
                  {s.value}
                </p>
                <p className="font-body text-zinc-400 leading-relaxed mt-4">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GHSolution() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative">
          <div
            className="absolute inset-0 blur-3xl opacity-50"
            style={{ background: `radial-gradient(circle, ${AMBER}22, transparent 62%)` }}
          />
          <img loading="lazy"
            src={ASSETS.actionDispense}
            alt="Safe Band Pro dispensing repellent from the wrist"
            className="relative w-full max-h-[70vh] object-contain mx-auto rounded-sm"
          />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <Overline>Active Daytime Protection</Overline>
          <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight leading-tight">
            A wearable that closes the gap bed nets can't reach.
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
            Safe Band Pro doesn't replace bed nets. It extends protection into
            the daytime hours they can't cover. Repellent lives on the wrist,
            applied in a single press, so people stay protected while they work,
            walk, farm and play.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px mt-10 bg-zinc-800 border border-zinc-800">
          {SOLUTION_FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="bg-[#121212] p-6 h-full">
                <f.icon size={20} strokeWidth={1.5} style={{ color: AMBER }} />
                <h3 className="font-heading text-base font-medium mt-4">{f.title}</h3>
                <p className="font-body text-sm text-zinc-500 leading-relaxed mt-2">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GHProtects() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>Who We Protect</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Built with dignity, for the people on the front line of exposure.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-16 bg-zinc-800 border border-zinc-800">
          {GH.protects.map((p, i) => {
            const Icon = ICONS[p.icon] || Users;
            return (
              <Reveal key={p.title} delay={(i % 4) * 0.08}>
                <div
                  data-testid={`protects-${i}`}
                  className="bg-[#121212] p-8 h-full hover:bg-[#161616] transition-colors duration-500"
                >
                  <div className="w-11 h-11 flex items-center justify-center" style={{ border: `1px solid ${AMBER}44`, color: AMBER }}>
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-medium mt-6">{p.title}</h3>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6">
          <Reveal delay={0.1}>
            <div className="relative border border-zinc-800 overflow-hidden bg-black group" data-testid="protects-photo">
              <ZoomImage
                src={GH.photos.children}
                alt="A community health worker fitting Safe Band Pro bands on schoolchildren under the trees"
                caption="School-based distribution · a health worker fitting bands for children"
                testId="protects-photo-zoom"
                className="w-full h-[380px] md:h-[600px] object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono text-xs text-zinc-600 mt-4">
              School-based distribution in a sub-Saharan community: protection fitted, understood and worn every day.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function GHRefill() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <Overline>An Eco-Friendly, Local Model</Overline>
        <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
          Fill up close to home. Carry protection all day.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-14 relative border border-zinc-800 overflow-hidden bg-[#0d0d0d] group" data-testid="eco-cycle">
          <ZoomImage
            src={GH.photos.cycle}
            alt="Hand-drawn Eco-Friendly Protection Cycle: community distribution, awareness, daily protection, local organic repellent, refillable module, refill station and reusable band"
            caption="The Eco-Friendly Protection Cycle · distribute, protect, refill, reuse"
            testId="eco-cycle-image"
            className="w-full max-h-[85vh] object-contain mx-auto"
          />
        </div>
        <p className="font-mono text-xs text-zinc-600 mt-4">
          The Eco-Friendly Protection Cycle. Click to enlarge.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-14 bg-zinc-800 border border-zinc-800">
        {GH.refill.map((r, i) => (
          <Reveal key={r.step} delay={(i % 4) * 0.08}>
            <div
              data-testid={`refill-${i}`}
              className="group relative bg-[#121212] p-8 h-full hover:bg-[#161616] transition-colors duration-500"
            >
              <span className="font-mono text-sm" style={{ color: AMBER }}>{r.step}</span>
              <h3 className="font-heading text-lg font-medium mt-3">{r.title}</h3>
              <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{r.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GHDistribution() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>Reaching People at Scale</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Distributed through the networks communities already trust.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16 bg-zinc-800 border border-zinc-800">
          {GH.distribution.map((d, i) => {
            const Icon = ICONS[d.icon] || Landmark;
            return (
              <Reveal key={d.title} delay={i * 0.1}>
                <div className="bg-[#121212] p-8 md:p-10 h-full">
                  <div className="w-11 h-11 flex items-center justify-center" style={{ border: `1px solid ${AMBER}44`, color: AMBER }}>
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-medium mt-6">{d.title}</h3>
                  <p className="font-body text-zinc-400 leading-relaxed mt-3">{d.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GHDownload() {
  return (
    <section className="border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal>
            <Overline>The Full Vision</Overline>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-2xl">
              An innovation in the fight to{" "}
              <span style={{ color: AMBER_SOFT }}>end malaria.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-xl">
              Two ways to go deeper. The Initiative brief lays out the vision, a
              refillable, wrist-worn repellent dispenser that closes the daytime
              protection gap. The scientific whitepaper details the evidence,
              research protocol, and WHO-aligned plan behind it.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={GH_PDF}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="global-health-download"
              className="group inline-flex items-center gap-3 font-body text-sm font-medium text-black px-8 py-4 mt-10 transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: AMBER }}
            >
              <Download size={16} />
              Download the Initiative Brief
            </a>
            <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600 mt-4">
              PDF · WearSpray Global Health Initiative
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <a
            href={GH_WHITEPAPER}
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
            data-testid="global-health-whitepaper-card"
          >
            <div className="relative border border-zinc-800 bg-[#0d0d0d] p-10 overflow-hidden transition-colors duration-500 group-hover:bg-[#121212]">
              <div
                className="absolute -inset-8 blur-3xl opacity-40 pointer-events-none"
                style={{ background: `radial-gradient(circle, ${AMBER}22, transparent 65%)` }}
              />
              <div className="relative">
                <div
                  className="w-14 h-14 flex items-center justify-center"
                  style={{ border: `1px solid ${AMBER}44`, color: AMBER }}
                >
                  <FileText size={24} strokeWidth={1.5} />
                </div>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-8">
                  Scientific Whitepaper
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-3">
                  Combating Malaria with WearSpray Technology
                </h3>
                <p className="font-body text-zinc-500 text-sm leading-relaxed mt-4">
                  Addressing outdoor and day-biting mosquitoes: the evidence
                  base, research protocol, and validation plan for a wearable
                  repellent dispenser.
                </p>
                <div className="flex flex-wrap gap-2 mt-8">
                  {["WHO-aligned", "Field study", "Refillable"].map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-800 text-zinc-400"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <span
                  className="inline-flex items-center gap-2 font-body text-sm mt-8 group-hover:gap-3 transition-all"
                  style={{ color: AMBER }}
                >
                  <Download size={15} /> Download PDF
                </span>
              </div>
            </div>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function GHCta() {
  return (
    <section className="border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.2em] uppercase mb-6" style={{ color: AMBER }}>
            For NGOs, Health Organizations & Partners
          </p>
          <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tighter leading-tight max-w-4xl mx-auto">
            Help us bring daytime protection to the communities that{" "}
            <span style={{ color: AMBER_SOFT }}>need it most.</span>
          </h2>
          <Link
            to="/contact"
            data-testid="global-health-cta"
            className="inline-flex items-center gap-2 font-body text-sm font-medium text-black px-8 py-4 mt-10 transition-colors"
            style={{ backgroundColor: AMBER }}
          >
            Start a Conversation <ArrowRight size={16} />
          </Link>
          <p className="font-body text-sm text-zinc-400 mt-6">
            Or email{" "}
            <a
              href="mailto:greg@wearspray.com"
              data-testid="global-health-email"
              className="underline underline-offset-4 transition-colors hover:text-white"
              style={{ color: AMBER_SOFT }}
            >
              greg@wearspray.com
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const POPULATIONS = [
  { icon: HeartHandshake, title: "Healthcare workers", body: "Immediate access to sanitization and topical care within demanding clinical environments." },
  { icon: Wheat, title: "Agricultural workers", body: "Wearable access to personal protection throughout long periods of outdoor exposure." },
  { icon: School, title: "School communities", body: "Simple, accessible protection designed around the realities of children, teachers, and daily routines." },
  { icon: Tent, title: "Outdoor workforces", body: "A wearable delivery platform for environments where traditional containers are inconvenient, forgotten, or inaccessible." },
  { icon: Users, title: "Community health programs", body: "A new distribution model for bringing protective formulations closer to the moment they are actually needed." },
  { icon: Bug, title: "Mosquito-exposed populations", body: "A potential platform for improving access to topical repellents as part of broader vector protection strategies." },
  { icon: Landmark, title: "Public sector initiatives", body: "A scalable architecture that could support health systems, municipalities, NGOs, employers, and public health organizations." },
];

function GHPlatformDeployed() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="gh-platform-deployed">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>A Platform Built to Be Deployed</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Protection works better when it is already with you. At population
            scale, that principle becomes even{" "}
            <span style={{ color: AMBER_SOFT }}>more powerful.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
            The same patented wearable architecture can potentially support
            organizations seeking to place essential personal care and protective
            formulations directly into the daily routines of defined populations.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {POPULATIONS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.06}>
              <div
                className="border border-zinc-800 bg-[#121212] p-8 h-full hover:border-zinc-700 transition-colors"
                data-testid={`gh-population-${i}`}
              >
                <div className="w-11 h-11 flex items-center justify-center" style={{ border: `1px solid ${AMBER}44`, color: AMBER }}>
                  <p.icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-light tracking-tight text-white mt-6">{p.title}</h3>
                <p className="font-body text-zinc-400 leading-relaxed mt-3">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GHPartnership() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="gh-partnership">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>Designed for Partnership at Scale</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
            You don't have to build a new ecosystem from the ground up.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
            WearSpray's architecture can potentially integrate with existing
            formulations, manufacturing capabilities, distribution systems,
            healthcare networks, and institutional programs.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5 max-w-3xl">
            That creates opportunities for organizations already serving large
            populations to transform products traditionally carried in bottles or
            containers into protection that can be worn, accessed, and applied in
            seconds.
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-12 border-l-2 pl-6 md:pl-8 max-w-3xl" style={{ borderColor: AMBER }}>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] text-white">
              The opportunity is not simply to introduce another product.
            </p>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] mt-2" style={{ color: AMBER_SOFT }}>
              It is to create a new point of access and a new point of action.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function GHIP() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="gh-ip">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>The IP Position</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Protected architecture.{" "}
            <span style={{ color: AMBER_SOFT }}>Multiple pathways.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-10 flex items-start gap-4 max-w-3xl">
            <ShieldCheck size={22} style={{ color: AMBER }} className="mt-1 shrink-0" />
            <p className="font-body text-zinc-300 text-lg leading-relaxed">
              WearSpray has secured patent protection around its wearable
              dispensing architecture and its evolution into broader wearable and
              smartwatch integrations.
            </p>
          </div>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
            The intellectual property creates the foundation for applications
            spanning personal care, healthcare, workforce protection, public
            health, and connected wearable technology.
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] mt-12 max-w-3xl" style={{ color: AMBER_SOFT }}>
            One protected architecture can support many formulations, many
            environments, and many deployment models.
          </p>
          <Link
            to="/patents"
            data-testid="gh-ip-patents-link"
            className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase mt-8 transition-opacity hover:opacity-80"
            style={{ color: AMBER }}
          >
            View the issued patents
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

const HC_STATS = [
  { value: 1, prefix: "1 in ", suffix: "", after: 31, afterOnly: true, display: "1 in 31", label: "hospital patients has a healthcare-associated infection on any given day." },
  { display: "~40%", label: "average hand-hygiene compliance reported in healthcare settings without intervention." },
  { display: "$28B+", label: "estimated annual cost of healthcare-associated infections in the United States." },
];

function StatReveal({ stat, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="border border-zinc-800 bg-[#121212] p-8 h-full flex flex-col"
      data-testid={`gh-healthcare-stat-${i}`}
    >
      <span className="font-heading text-4xl md:text-5xl font-light tracking-tighter" style={{ color: AMBER_SOFT }}>
        {stat.display}
      </span>
      <p className="font-body text-zinc-400 leading-relaxed mt-4">{stat.label}</p>
    </motion.div>
  );
}

const HC_STEPS = ["Touch a shared surface.", "Dispense.", "Sanitize.", "Continue."];

const HC_PHOTOS = [
  { src: `${A}5j15tqap_nurse%20banner.jpg`, alt: "A nurse in navy scrubs on a busy hospital floor, raising her wrist to show a magenta WearSpray band", caption: "Access at the point of care" },
  { src: `${A}ophsyqat_nurse%20wheelchair.jpg`, alt: "A nurse wearing a WearSpray band pushing a patient in a wheelchair down a hospital hallway", caption: "The sanitizer moves with the caregiver" },
  { src: `${A}cfqppqeg_nurse%207%20magenta.jpg`, alt: "A hospital nurse pressing the glowing magenta face of her WearSpray band on a busy ward", caption: "Ready between every patient" },
];

const HC_SETTINGS = [
  "Healthcare worker trials",
  "Infection prevention studies",
  "Hospital pilot programs",
  "Outpatient environments",
  "Long-term care facilities",
  "Home healthcare programs",
];

function GHHealthcare() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a] relative overflow-hidden" data-testid="gh-healthcare">
      {/* soft amber bloom */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-[0.12] blur-3xl"
        style={{ background: `radial-gradient(circle, ${AMBER} 0%, transparent 70%)` }}
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 relative">
        {/* Header */}
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <Overline>Healthcare · Ready to Explore Now</Overline>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Protection at the{" "}
            <span style={{ color: AMBER_SOFT }}>point of need.</span>
          </h2>
          <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white mt-6 max-w-3xl">
            The sanitizer goes where the caregiver goes.
          </p>
        </Reveal>

        {/* The problem */}
        <Reveal delay={0.08}>
          <div className="mt-8 max-w-3xl space-y-5">
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              Healthcare professionals move constantly, between patients,
              equipment, workstations, doors, carts, monitors, shared surfaces
              and treatment areas. Each interaction can create another moment
              when hand hygiene matters.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              Research across millions of hand-hygiene opportunities shows
              compliance can decline as shifts progress and fatigue builds. Yet
              healthcare-associated infections still affect hundreds of thousands
              of U.S. patients each year, adding billions in costs.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-10 border-l-2 pl-6 max-w-3xl" style={{ borderColor: AMBER }}>
            <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white">
              The challenge is not simply awareness.
            </p>
            <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug mt-2" style={{ color: AMBER_SOFT }}>
              It is access at the moment action is required.
            </p>
          </div>
        </Reveal>

        {/* Data anchor, HAI / compliance stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-14" data-testid="gh-healthcare-stats">
          {HC_STATS.map((s, i) => (
            <StatReveal key={s.display} stat={s} i={i} />
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="font-mono text-xs text-zinc-600 mt-4 max-w-3xl leading-relaxed">
            Figures reflect widely cited public-health estimates (e.g., U.S. CDC / WHO) and are provided for context.
          </p>
        </Reveal>

        {/* Healthcare photos supplied by user */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-16">
          {HC_PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={i * 0.08}>
              <div className="relative border border-zinc-800 overflow-hidden bg-black group" data-testid={`gh-healthcare-photo-${i}`}>
                <ZoomImage
                  src={p.src}
                  alt={p.alt}
                  caption={p.caption}
                  testId={`gh-healthcare-photo-zoom-${i}`}
                  className="w-full h-[420px] md:h-[480px] object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                <span className="absolute bottom-4 left-4 right-4 font-mono text-xs tracking-[0.12em] uppercase text-zinc-300">
                  {p.caption}
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        {/* From fixed dispenser to personal access */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-12 items-start">
          <Reveal>
            <div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
                From a Fixed Dispenser to Personal Access
              </span>
              <div className="mt-6 space-y-5 max-w-xl">
                <p className="font-body text-zinc-400 text-lg leading-relaxed">
                  Traditional hand-hygiene infrastructure depends largely on
                  sinks and fixed sanitizer dispensers positioned throughout a
                  facility. WearSpray introduces another access point.
                </p>
                <p className="font-body text-zinc-400 text-lg leading-relaxed">
                  For healthcare environments and personnel where wearable
                  devices are appropriate, WearSpray can carry an approved
                  sanitizing formulation directly on the wrist. Instead of
                  requiring the worker to locate the next dispenser, the
                  sanitizer moves with the caregiver.
                </p>
              </div>
            </div>
          </Reveal>

          {/* The staccato flow, the "magic" beat */}
          <Reveal delay={0.1}>
            <div className="border border-zinc-800 bg-[#121212] p-8 md:p-10">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-6">
                One continuous motion
              </span>
              <div className="space-y-4">
                {HC_STEPS.map((s, i) => (
                  <motion.div
                    key={s}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{ duration: 0.5, delay: i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-4"
                    data-testid={`gh-healthcare-step-${i}`}
                  >
                    <span
                      className="font-mono text-xs w-7 h-7 flex items-center justify-center rounded-full border shrink-0"
                      style={{ borderColor: AMBER, color: AMBER }}
                    >
                      {i + 1}
                    </span>
                    <span className="font-heading text-2xl md:text-3xl font-light tracking-tight text-white">
                      {s}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Ready to explore now */}
        <Reveal>
          <div className="mt-24">
            <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
              Ready to Explore Now
            </span>
            <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-tight max-w-4xl mt-5">
              This use case does not require the invention of a{" "}
              <span style={{ color: AMBER_SOFT }}>new behavior.</span>
            </h3>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              Healthcare professionals already sanitize their hands repeatedly
              throughout the day. WearSpray simply changes where access to that
              sanitizer can occur, so it could be evaluated now, across a range
              of appropriate clinical settings.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10">
          {HC_SETTINGS.map((s, i) => (
            <Reveal key={s} delay={(i % 3) * 0.06}>
              <div
                className="border border-zinc-800 bg-[#121212] px-5 py-6 h-full hover:border-zinc-700 transition-colors"
                data-testid={`gh-healthcare-setting-${i}`}
              >
                <span className="font-body text-zinc-300 leading-snug">{s}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-10 max-w-3xl">
            A clinical study could examine whether immediate wearable access
            improves hand-hygiene frequency, reduces missed hygiene
            opportunities, and supports compliance during periods of high
            workload.
          </p>
        </Reveal>

        {/* Whitepaper, protection within reach: hand hygiene friction */}
        <Reveal delay={0.06}>
          <div
            className="mt-16 border border-zinc-800 bg-[#121212] p-8 md:p-12"
            data-testid="gh-healthcare-whitepaper-brief"
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3">
                <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
                  Whitepaper · WearSpray Research
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white mt-4">
                  Protection Within Reach
                </h3>
                <p className="font-body text-zinc-400 leading-relaxed mt-3">
                  Hand hygiene friction in hospitals and home care, and the case
                  for closing the distance between a hygiene opportunity and the
                  ability to act.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    "The barrier is rarely knowledge. It's friction, the distance, effort and access that stand between intent and a clean hand.",
                    "An estimated 518,000 healthcare-associated infections occurred in U.S. hospitals in 2023, most unrelated to any device.",
                    "In home care, 83.6% of nurses' bags tested positive for pathogens, a reminder that where sanitizer lives matters too.",
                  ].map((pt, i) => (
                    <li key={pt} className="flex gap-3" data-testid={`gh-whitepaper-point-${i}`}>
                      <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: AMBER }} />
                      <span className="font-body text-zinc-300 leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-2 flex flex-col gap-5 lg:border-l lg:border-zinc-800 lg:pl-10">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 flex items-center justify-center border border-zinc-700" style={{ color: AMBER }}>
                    <FileText size={22} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono text-xs text-zinc-500 leading-relaxed">
                    PDF · WearSpray Research
                    <br />Whitepaper
                  </div>
                </div>
                <p className="font-body text-zinc-400 text-sm leading-relaxed">
                  Read the full analysis of hospital and home care friction,
                  the cost of compliance decline, and a proposed pilot for
                  wearable, on-wrist access.
                </p>
                <a
                  href="/docs/WearSpray_Protection_Within_Reach_Hand_Hygiene_Whitepaper.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gh-healthcare-whitepaper-download"
                  className="group inline-flex items-center justify-center gap-3 px-6 py-4 font-mono text-sm tracking-[0.12em] uppercase text-black transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: AMBER }}
                >
                  <Download size={16} />
                  Download the Whitepaper
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Research brief, the afternoon compliance gap */}
        <Reveal delay={0.08}>
          <div
            className="mt-8 border border-zinc-800 bg-[#121212] p-8 md:p-12"
            data-testid="gh-healthcare-research-brief"
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
              <div className="lg:col-span-3">
                <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
                  Research Brief · WearSpray Research
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white mt-4">
                  The Afternoon Compliance Gap
                </h3>
                <p className="font-body text-zinc-400 leading-relaxed mt-3">
                  Rethinking the timing of hand hygiene in healthcare, and the
                  case for wearable, on-wrist sanitization.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    "Observed hand-hygiene compliance typically falls in the 40–60% range, and lower still in high-acuity settings.",
                    "In one ICU study, compliance dropped from day to night across every provider type, physicians 81% → 46%, nurses 64% → 55%.",
                    "Adherence declines by roughly 5% for every additional 10 hand-hygiene opportunities per hour once demand climbs.",
                  ].map((pt, i) => (
                    <li key={pt} className="flex gap-3" data-testid={`gh-brief-point-${i}`}>
                      <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: AMBER }} />
                      <span className="font-body text-zinc-300 leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-2 flex flex-col gap-5 lg:border-l lg:border-zinc-800 lg:pl-10">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 flex items-center justify-center border border-zinc-700" style={{ color: AMBER }}>
                    <FileText size={22} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono text-xs text-zinc-500 leading-relaxed">
                    PDF · August 2026
                    <br />Research Brief
                  </div>
                </div>
                <p className="font-body text-zinc-400 text-sm leading-relaxed">
                  Read the full analysis of when compliance breaks down across the
                  clinical day, and why access at the wrist changes the equation.
                </p>
                <a
                  href="/docs/WearSpray_Afternoon_Compliance_Gap_Research_Brief.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gh-healthcare-brief-download"
                  className="group inline-flex items-center justify-center gap-3 px-6 py-4 font-mono text-sm tracking-[0.12em] uppercase text-black transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: AMBER }}
                >
                  <Download size={16} />
                  Download the Brief
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* The opportunity */}
        <Reveal>
          <div className="mt-24 border-t border-zinc-800 pt-16">
            <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
              The Opportunity
            </span>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              Healthcare has spent decades improving hand hygiene through
              education, monitoring, signage, sinks, and wall-mounted dispensers.
              WearSpray asks one additional question:
            </p>
            <p className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug text-white mt-8 max-w-4xl">
              What happens when the dispenser moves{" "}
              <span style={{ color: AMBER_SOFT }}>with the healthcare professional?</span>
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-3xl">
              By reducing the distance between a hygiene opportunity and the
              ability to act, WearSpray could create a new layer of personal
              access within infection prevention.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.05] mt-16 max-w-4xl amber-shimmer">
            Protection should be available when the moment happens.
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
            <Link
              to="/contact"
              data-testid="gh-healthcare-pilot-cta"
              className="group inline-flex items-center gap-3 px-8 py-4 font-mono text-sm tracking-[0.12em] uppercase text-black transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: AMBER }}
            >
              Explore a Pilot
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <span className="font-body text-zinc-500 text-sm max-w-sm leading-relaxed">
              For hospitals, infection-prevention teams and clinical researchers evaluating wearable access.
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}


function GHMalariaIntro() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="gh-malaria-intro">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>Application in Focus · Malaria</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            One application in depth: closing the{" "}
            <span style={{ color: AMBER_SOFT }}>daytime malaria gap.</span>
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
            The same platform can be applied across many needs. Here is one of
            them, explored in depth, Safe Band Pro, a refillable, wrist-worn
            repellent dispenser designed to improve access to repellent through
            the daylight hours that bed nets don't cover.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const HUMAN_NEED_CARDS = [
  { eyebrow: "Sun Exposure", body: "Millions of people spend significant time outdoors, where consistent sunscreen access and reapplication can be difficult.", action: "Potential point of action: wearable sunscreen access." },
  { eyebrow: "Mosquito Exposure", body: "Large populations around the world live with ongoing exposure to mosquito borne disease.", action: "Potential point of action: wearable repellent access." },
  { eyebrow: "Hand Hygiene", body: "Healthcare, schools, workplaces, and public environments depend heavily on frequent hand hygiene.", action: "Potential point of action: immediate access to sanitizer." },
  { eyebrow: "Skin & Personal Care", body: "Topical products create value only when they are available and used consistently.", action: "Potential point of action: skincare, lotion, serum, and protective formulations on the wrist." },
  { eyebrow: "Future Healthcare", body: "Some health situations depend not only on knowing what is happening, but on having the appropriate response immediately accessible.", action: "Future point of action: connected wearable intelligence paired with a physical formulation, subject to development, testing, validation, and appropriate regulatory requirements." },
];

function GHHumanNeed() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="gh-human-need">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>The Human Need</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
            Millions of moments.{" "}
            <span style={{ color: AMBER_SOFT }}>One point of action.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-8 max-w-3xl space-y-5">
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              Across public health, personal protection, skincare, sanitation,
              environmental exposure, and future healthcare applications, the
              challenge is often the same.
            </p>
            <div className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white">
              <p>The formulation may already exist.</p>
              <p>The science may already exist.</p>
              <p>The recommendation may already exist.</p>
            </div>
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              But the product isn&rsquo;t always there when the person needs it.
              WearSpray is designed around that moment: by turning useful liquids,
              lotions, gels, and mists from something you must remember to carry
              into something you wear, the platform creates a new point of access
             , and a potential new point of action.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {HUMAN_NEED_CARDS.map((c, i) => (
            <Reveal key={c.eyebrow} delay={(i % 3) * 0.06}>
              <div
                className="border border-zinc-800 bg-[#121212] p-8 h-full hover:border-zinc-700 transition-colors flex flex-col"
                data-testid={`gh-human-need-${i}`}
              >
                <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
                  {c.eyebrow}
                </span>
                <p className="font-body text-zinc-400 leading-relaxed mt-5">{c.body}</p>
                <p className="font-body text-sm text-zinc-500 leading-relaxed mt-auto pt-6">
                  {c.action}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GHNextEvolution() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="gh-next-evolution">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <Overline>The Next Evolution</Overline>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
            From access today to{" "}
            <span style={{ color: AMBER_SOFT }}>intelligent action tomorrow.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-8 max-w-3xl space-y-5">
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              WearSpray already provides the physical access layer.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed">
              As wearable technology continues to evolve, future connected systems
              could potentially combine sensing, alerts, and contextual
              intelligence with a formulation that is already being worn.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.05] mt-14" style={{ color: AMBER_SOFT }}>
            Sense. Understand. Prompt. Act. Learn.
          </p>
          <p className="font-body text-sm text-zinc-600 leading-relaxed mt-6 max-w-2xl">
            Future connected wearable applications remain subject to product
            development, testing, technical integration, and applicable
            regulatory requirements.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function GHMarketOpportunity() {
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="gh-market-opportunity">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          {/* Copy column */}
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>Market Opportunity · High-Density Markets</Overline>
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
                Unlocking Scalable Protection in{" "}
                <span style={{ color: AMBER_SOFT }}>High-Density Markets.</span>
              </h2>
              <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white mt-6 max-w-2xl">
                A high-margin hardware platform built for high-volume FMCG distribution.
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-8 space-y-5 max-w-2xl">
                <p className="font-body text-zinc-400 text-lg leading-relaxed">
                  Continuous protection against pathogens and vector-borne
                  diseases fails at the point of reapplication, not formulation.
                  WearSpray&rsquo;s patented wearable delivery system bridges this
                  compliance gap through ultra-low-cost, refillable hardware
                  designed for localized manufacturing at the point of deployment.
                </p>
                <p className="font-body text-zinc-400 text-lg leading-relaxed">
                  By pairing wearable hardware with high-margin bulk refill
                  consumable ecosystems, WearSpray enables nationwide retail
                  networks and personal care leaders to turn single-use packaging
                  into recurring customer lifetime value.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <a
                href={GH_MARKET_PDF}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="gh-market-download"
                className="group inline-flex items-center gap-3 font-body text-sm font-medium text-black px-8 py-4 mt-10 transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: AMBER }}
              >
                <Download size={16} />
                Download the Market Opportunity &amp; Strategy Brief
              </a>
              <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600 mt-4">
                PDF · Market Opportunity &amp; Strategy Brief
              </p>
              <Link
                to="/market"
                data-testid="gh-market-opportunity-link"
                className="group inline-flex items-center gap-2 font-body text-sm text-zinc-300 hover:text-white transition-colors mt-8 border-b border-zinc-700 pb-1 hover:border-zinc-400"
              >
                See the addressable audience for the U.S., Europe &amp; Caribbean
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          {/* COGS pull-stat callout */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="relative border border-zinc-800 bg-[#121212] p-10 h-full flex flex-col justify-center overflow-hidden" data-testid="gh-market-cogs">
                <div
                  className="absolute -inset-8 blur-3xl opacity-40 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 70% 30%, ${AMBER}22, transparent 65%)` }}
                />
                <div className="relative">
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
                    Projected Unit Cost at Scale
                  </span>
                  <p className="font-heading text-6xl md:text-7xl font-light tracking-tighter mt-4" style={{ color: AMBER_SOFT }}>
                    ~$0.94
                  </p>
                  <p className="font-body text-zinc-400 leading-relaxed mt-5 max-w-sm">
                    Refillable hardware projected at low unit cost when
                    manufactured locally at the point of deployment.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-8">
                    {["Refillable hardware", "Local manufacturing", "Recurring refills"].map((t) => (
                      <span
                        key={t}
                        className="font-mono text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-800 text-zinc-400"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

const INDIA_CATEGORIES = [
  "Personal hygiene",
  "Insect repellent",
  "Sun care",
  "Skincare and serums",
  "Fragrance and body mist",
  "Enterprise and occupational protection",
];

const INDIA_DEPLOY = [
  "Healthcare workers",
  "Logistics personnel",
  "Transit employees",
  "Agricultural & outdoor workers",
  "School populations",
  "Municipal teams",
  "Consumer retail programs",
  "Public health initiatives",
];

const INDIA_TOPICS = [
  {
    eyebrow: "Vector-Borne Disease Exposure",
    paras: [
      "Protection against mosquito-borne disease is not limited to nighttime or residential environments. Day-biting mosquitoes, including Aedes species associated with dengue and chikungunya, can create exposure during commuting, school hours, outdoor work, and routine daily activity.",
      "Topical repellents are already available. The behavioral challenge is continued access and repeated application. When protection depends on carrying a separate bottle, compliance may decline precisely when the user is away from home and moving through higher-exposure environments.",
      "WearSpray is designed to address this access gap by placing a refillable dispensing system directly on the wrist.",
    ],
  },
  {
    eyebrow: "Hand Hygiene & Point-of-Action Access",
    paras: [
      "Hand sanitization presents a similar behavioral challenge. Awareness of hygiene may be high, but the ability to act immediately can depend on whether sanitizer is physically available at the moment of need.",
      "In crowded transit systems, marketplaces, schools, healthcare environments, logistics operations, and other high-contact settings, carrying and repeatedly retrieving a separate container introduces friction.",
      "WearSpray shifts sanitizer access from a carried object to an on-body dispensing format, reducing the number of steps between awareness and application.",
    ],
  },
  {
    eyebrow: "Localized Manufacturing Potential",
    paras: [
      "The platform architecture is designed around molded components, refillable reservoirs, and repeat use. This creates the potential to evaluate localized manufacturing in India at progressively larger production volumes.",
      "Localized production could support lower landed costs, reduced transportation complexity, faster replenishment, and greater flexibility for regional distribution partners.",
      "Current cost figures should be treated as projected production economics until validated through regional tooling, material sourcing, manufacturing quotations, and scaled production runs.",
    ],
  },
  {
    eyebrow: "Recurring Refill Economics",
    paras: [
      "The larger opportunity is not limited to the wearable hardware. WearSpray separates the durable dispensing device from the consumable formulation.",
      "This creates the potential for an installed base of reusable hardware supported by recurring purchases of sanitizer, insect repellent, sunscreen, skincare, and other compatible formulations.",
      "For a major FMCG, healthcare, or retail partner, the platform can therefore function as both a dispensing system and a recurring consumable ecosystem.",
    ],
  },
];

function GHIndia() {
  return (
    <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="gh-india">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Intro, text + India visual anchor */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>India · High-Density Market Opportunity</Overline>
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
                A uniquely important environment to test the premise at{" "}
                <span style={{ color: AMBER_SOFT }}>meaningful scale.</span>
              </h2>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-2xl">
                India presents a compelling environment for evaluating wearable,
                on-demand access to personal protection. High population density,
                extensive public transit use, outdoor labor, school activity,
                healthcare work, and daily movement through crowded public
                environments can increase the importance of convenient
                reapplication.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="relative border border-zinc-800 overflow-hidden bg-black">
              <div
                className="absolute -inset-6 blur-3xl opacity-40 pointer-events-none"
                style={{ background: `radial-gradient(circle, ${AMBER}22, transparent 65%)` }}
              />
              <img loading="lazy"
                src={`${A}18fq63g3_nda%20healtih%20care%20.jpg`}
                alt="A community health worker in India speaking with a mother and child, wearing a magenta WearSpray band on her wrist"
                className="relative w-full h-[320px] md:h-[440px] object-cover object-center"
                data-testid="gh-india-image"
              />
            </div>
          </Reveal>
        </div>

        {/* Four narrative topics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-16 bg-zinc-800 border border-zinc-800">
          {INDIA_TOPICS.map((t, i) => (
            <Reveal key={t.eyebrow} delay={(i % 2) * 0.08}>
              <div className="bg-[#121212] p-8 md:p-10 h-full" data-testid={`gh-india-topic-${i}`}>
                <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: AMBER }}>
                  {t.eyebrow}
                </span>
                <div className="mt-5 space-y-4">
                  {t.paras.map((p, j) => (
                    <p key={j} className="font-body text-zinc-400 leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* The Scale Opportunity */}
        <Reveal>
          <div className="mt-24">
            <Overline>The Scale Opportunity</Overline>
            <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug max-w-4xl">
              WearSpray does not require the creation of a new consumable
              category. It introduces a new{" "}
              <span style={{ color: AMBER_SOFT }}>delivery architecture</span>{" "}
              across categories that already have recurring demand.
            </h3>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {INDIA_CATEGORIES.map((c, i) => (
            <Reveal key={c} delay={(i % 3) * 0.06}>
              <div
                className="border border-zinc-800 bg-[#121212] px-5 py-6 h-full hover:border-zinc-700 transition-colors"
                data-testid={`gh-india-category-${i}`}
              >
                <span className="font-body text-zinc-300 leading-snug">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.08}>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-10 max-w-3xl">
            The strategic question is therefore not whether consumers already use
            these formulations. The question is whether immediate, wearable access
            can improve convenience, reapplication behavior, and recurring
            consumption across selected use cases.
          </p>
        </Reveal>

        {/* Potential Deployment Environments */}
        <Reveal>
          <div className="mt-24">
            <Overline>Potential Deployment Environments</Overline>
            <p className="font-body text-zinc-400 text-lg leading-relaxed max-w-3xl">
              Initial evaluation could span a range of high-contact, high-movement
              settings, identifying where wearable access creates the
              greatest measurable improvement in convenience, compliance, refill
              frequency, and overall economics.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {INDIA_DEPLOY.map((d, i) => (
            <Reveal key={d} delay={(i % 4) * 0.06}>
              <div
                className="border border-zinc-800 bg-[#121212] px-5 py-6 h-full hover:border-zinc-700 transition-colors"
                data-testid={`gh-india-deploy-${i}`}
              >
                <span className="font-body text-zinc-300 text-sm md:text-base leading-snug">{d}</span>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Platform Hypothesis, closing */}
        <Reveal delay={0.1}>
          <div className="mt-24 border-l-2 pl-6 md:pl-8 max-w-4xl" style={{ borderColor: AMBER }}>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-6">
              Platform Hypothesis
            </span>
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light tracking-tight leading-[1.2] text-white">
              When protection is physically present at the point of action, the
              probability of using it may increase.
            </p>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug mt-6" style={{ color: AMBER_SOFT }}>
              India offers a uniquely important environment in which to test that
              premise at meaningful scale.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function GlobalHealth() {
  return (
    <div data-testid="global-health-page">
      <SEO {...PAGE_SEO["/global-health"]} path="/global-health" />
      {/* Broad platform + commercialization story */}
      <GHHero />
      <GHPrecursor />
      <GHPlatformDeployed />
      <GHPartnership />
      <GHIP />

      {/* GLOBAL REACH, earth showcase video banner */}
      <section className="relative w-full bg-black border-y border-zinc-800 overflow-hidden" data-testid="gh-earth-banner">
        <EarthShowcase />
      </section>

      {/* Market opportunity, high-density markets (directly under the globe) */}
      <GHMarketOpportunity />

      {/* India, high-density market deep dive */}
      <GHIndia />


      {/* Strategic platform logic, the human need + the next evolution */}
      <GHHumanNeed />
      <GHNextEvolution />

      {/* Healthcare: ready-now application (above malaria) */}
      <GHHealthcare />

      {/* Flagship application: malaria (all malaria content consolidated here) */}
      <GHMalariaIntro />
      <GHStats />
      <GHSolution />
      <GHProtects />
      <GHRefill />
      <GHDistribution />
      <GHDownload />

      <GHCta />
    </div>
  );
}
